import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { Button } from "../Button";
import Tree from "@ecologi/assets/icons/tree.svg";
import Dac from "@ecologi/assets/icons/dac.svg";
import Flowers from "@ecologi/assets/icons/flowers.svg";
import Turbine from "@ecologi/assets/icons/turbine-yellow-tips.svg";
import { getPhoenixUrl, ImpactType } from "@ecologi/shared";
import useMedia from "react-use/lib/useMedia";
import {
  buttercupLighter,
  jungleLighter,
  lavenderLighter,
  roseLighter,
} from "../theme/ecologi-colors";

const CardContainer = styled.div<{ background: string }>`
  height: 232px;
  background: ${({ background }) => background};
  border-radius: 24px;
  padding: 33px 0px 33px 25px;
  display: grid;
  flex-direction: column;
  justify-content: space-between;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    "title logo"
    "link logo";
`;

const LinkButton = styled(Button)`
  padding: 10px 16px 10px 16px;
  font-size: 16px;
`;

const Title = styled.h3`
  grid-area: title;
  font-size: 22px;
  font-weight: 800;
  line-height: 26px;
  @media ${({ theme }) => theme.bp.tabletDown} {
    font-size: 18px;
  }
`;

const ButtonContainer = styled.div`
  grid-area: link;
  display: flex;
  align-items: end;
  @media only screen and (max-width: 360px) {
    max-width: 134px;
  }
`;

const ImageContainer = styled.div`
  grid-area: logo;
  display: flex;
  align-items: end;
  margin-left: 16px;
`;

const ImpactTypeAsset: Record<
  ImpactType,
  {
    buttonLabel: string;
    image: ReactNode;
    link: string;
    background: string;
  }
> = {
  [ImpactType.CARBON_OFFSETS]: {
    buttonLabel: "Shop carbon avoidance",
    image: <Turbine />,
    link: `${getPhoenixUrl(
      process.env.NEXT_PUBLIC_DEPLOYMENT
    )}/impact-marketplace/carbon-avoidance`,
    background: buttercupLighter,
  },
  [ImpactType.TREES]: {
    buttonLabel: "Shop tree planting",
    image: <Tree />,
    link: `${getPhoenixUrl(
      process.env.NEXT_PUBLIC_DEPLOYMENT
    )}/impact-marketplace/reforestation`,
    background: jungleLighter,
  },
  [ImpactType.CARBON_REMOVAL]: {
    buttonLabel: "Shop carbon removal",
    image: <Dac />,
    link: `${getPhoenixUrl(
      process.env.NEXT_PUBLIC_DEPLOYMENT
    )}/impact-marketplace/carbon-removals`,
    background: lavenderLighter,
  },
  [ImpactType.HABITAT_RESTORATION]: {
    buttonLabel: "Shop habitat restoration",
    image: <Flowers />,
    link: `${getPhoenixUrl(
      process.env.NEXT_PUBLIC_DEPLOYMENT
    )}/impact-marketplace/habitat-restoration`,
    background: roseLighter,
  },
};

export const DiversifyImpactCard = ({
  impactType,
}: {
  impactType: ImpactType;
}) => {
  const isMobile = useMedia("(max-width: 768px)");

  return (
    <CardContainer background={ImpactTypeAsset[impactType].background}>
      <Title>Browse our impact marketplace to get started</Title>
      <ButtonContainer>
        <LinkButton
          small={isMobile}
          darkGreen
          text={ImpactTypeAsset[impactType].buttonLabel}
          href={ImpactTypeAsset[impactType].link}
        />
      </ButtonContainer>
      <ImageContainer>{ImpactTypeAsset[impactType].image}</ImageContainer>
    </CardContainer>
  );
};
